// Client-owned Discover constants.
//
// The category option array is the chip label map for known topic values. Communities do not use it.
// Language and difficulty chips use the shared course tag locale keys (`library.myCourseCard.tags.*`).
// A course tag value without a locale key still renders from the API label.

import type { DiscoverFilterOption } from '../types/discover'

export const DISCOVER_CATEGORY_OPTIONS: DiscoverFilterOption[] = [
  { value: 'design', labelKey: 'discover.options.category.design' },
  { value: 'business', labelKey: 'discover.options.category.business' },
  { value: 'technology', labelKey: 'discover.options.category.technology' },
  { value: 'gaming', labelKey: 'discover.options.category.gaming' },
  { value: 'wellness', labelKey: 'discover.options.category.wellness' },
  { value: 'literature', labelKey: 'discover.options.category.literature' },
]

/** Placeholder cards drawn while the first server page is in flight. Two full rows at `lg`. */
export const DISCOVER_INITIAL_SKELETON_COUNT = 6

/** Placeholder cards drawn under the grid while a *further* page is in flight. One row at `lg`. */
export const DISCOVER_LOADING_MORE_SKELETON_COUNT = 3

/** Course tab runs a 4-column grid at `xl`, so its skeletons fill whole rows there instead. */
export const DISCOVER_COURSE_INITIAL_SKELETON_COUNT = 8

export const DISCOVER_COURSE_LOADING_MORE_SKELETON_COUNT = 4

/**
 * Rows requested per server page.
 *
 * Sized well above one viewport of cards: the feed is an uncapped infinite scroll, so a larger page
 * buys several screens of scrolling per request instead of a fetch every row. It also matters for
 * the client-side search and free filter, which narrow the *loaded* window only — a small page
 * would routinely yield zero matches and make the sentinel walk the feed one thin page at a time.
 */
export const DISCOVER_API_PAGE_SIZE = 24

/**
 * Page size for the in-app upsell feed merged into every course Discover surface (via
 * `useCourseDiscoverFeed`) and the bundle checkout page, so they all share one cached upsell query.
 */
export const DISCOVER_UPSELL_PAGE_SIZE = 12

/**
 * While search / Free / tags are active the loaded window can stay short of a screenful, which keeps
 * the sentinel in view and would otherwise walk the whole catalogue page after page. Allow this many
 * automatic fetches per filter change, then hand control to an explicit "Show more results" button.
 */
export const DISCOVER_FILTERED_AUTO_LOAD_PAGE_LIMIT = 3

/** URL param backing the top-bar search, shared by the search context and the filter composable. */
export const DISCOVER_SEARCH_QUERY_KEY = 'q'

/**
 * URL param choosing the tab Discover opens on (e.g. `/discover?tab=courses` from the `/courses`
 * "See all" link). Values are the user-facing plural names, mapped to the internal entity type.
 */
export const DISCOVER_TAB_QUERY_KEY = 'tab'

export const DISCOVER_TAB_QUERY_VALUES = { community: 'communities', course: 'courses' } as const
